import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import useSsoSilentAttempt from './hooks/useSsoSilentAttempt';
import Login from './screens/Login';
import Home from './screens/Home';
import Integrations from './screens/Integrations';
import Resources from './screens/Resources';
import Backups from './screens/Backups';
import PageAccess from './screens/PageAccess';
import Directory from './screens/Directory';
import AbsenceReportingDashboard from './screens/AbsenceReportingDashboard';
import AbsenceReportingCategory from './screens/AbsenceReportingCategory';
import SimManager from './screens/SimManager';
import DeviceRegister from './screens/DeviceRegister';
import PhoneRegister from './screens/PhoneRegister';
import DevicesToCollect from './screens/DevicesToCollect';
import AteraAgents from './screens/AteraAgents';
import WazuhAgents from './screens/WazuhAgents';
import VanOrders from './screens/VanOrders';
import StockLevels from './screens/StockLevels';
import StockReset from './screens/StockReset';
import StockAnalytics from './screens/StockAnalytics';
import DailyInstallations from './screens/DailyInstallations';
import Leaderboard from './screens/Leaderboard';
import OperationsReporting from './screens/OperationsReporting';
import InstallCount from './screens/InstallCount';
import CommercialSurveys from './screens/CommercialSurveys';
import Walkarounds from './screens/Walkarounds';
import FleetRegister from './screens/FleetRegister';
import CxRegister from './screens/CxRegister';
import PoleSurvey from './screens/PoleSurvey';
import PoleMap from './screens/PoleMap';
import PoleSurveyLog from './screens/PoleSurveyLog';
import ClimbCheck from './screens/ClimbCheck';
import JobImports from './screens/JobImports';
import StockLoggingKpis from './screens/StockLoggingKpis';
import TargetPerformance from './screens/TargetPerformance';
import InstallationMaster from './screens/InstallationMaster';
import WhereaboutsMaster from './screens/WhereaboutsMaster';
import SupportRequests from './screens/SupportRequests';
import InternalRemediations from './screens/InternalRemediations';
import SafetyCompliance from './screens/SafetyCompliance';
import CustomerExperience from './screens/CustomerExperience';
import StockCheck from './screens/StockCheck';
import StockVariances from './screens/StockVariances';
import CommsDispatch from './screens/CommsDispatch';
import MessageAnalytics from './screens/MessageAnalytics';
import RecipientActivity from './screens/RecipientActivity';
import CommsInbox from './screens/CommsInbox';
import MfaCallList from './screens/MfaCallList';
import JobCardStock from './screens/JobCardStock';
import StockDisputes from './screens/StockDisputes';
import VanTransfers from './screens/VanTransfers';
import IspStockCounts from './screens/IspStockCounts';
import JobCardStockOrder from './screens/JobCardStockOrder';
import VanStock from './screens/VanStock';
import JobCards from './screens/JobCards';
import StockProcurement from './screens/StockProcurement';
import ProcurementApprovals from './screens/ProcurementApprovals';
import Receiving from './screens/Receiving';
import BackOrders from './screens/BackOrders';
import ItSupport from './screens/ItSupport';
import SupportConsole from './screens/SupportConsole';
import AssistantReview from './screens/AssistantReview';
import Inventory from './screens/Inventory';
import Products from './screens/Products';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import ManagerAbsenceReporting from './screens/ManagerAbsenceReporting';

function App() {
  const ssoAttempted = useSsoSilentAttempt();

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Home />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Company directory — any signed-in user. Field-level visibility is
            enforced server-side, so this route needs no gate of its own. */}
        <Route
          path="/directory"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Directory />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Absence & late reporting HR portal — the office/HR web side of the
            engineer-facing FSM absence flow. Data is gated by the FSM server
            (HR / full-admin), so these routes need no gate of their own. */}
        <Route
          path="/absence-reporting"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AbsenceReportingDashboard />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/absence-reporting/:category"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AbsenceReportingCategory />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Managers > Absence Reporting — gated by the dashboard proxy + FSM
            (absenceReporting group; a manager sees only what they reported). */}
        <Route
          path="/managers/absence-reporting"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ManagerAbsenceReporting />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* SIM manager — Logistics own the SIMs; the server gates it. */}
        <Route
          path="/sims"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <SimManager />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Device register — Logistics own the estate; the server gates it. */}
        <Route
          path="/devices"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <DeviceRegister />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Phone register — mobile estate synced from Miradore; server-gated
            (same assetManagement gate as the device register). */}
        <Route
          path="/phones"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PhoneRegister />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Live agent views — read from Atera and Wazuh on every refresh, so an
            enrolment can be confirmed without signing into either console.
            Server-gated to IT Administration or Asset Management. */}
        <Route
          path="/devices/atera"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AteraAgents />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/devices/wazuh"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <WazuhAgents />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Van stock orders — office view of what engineers asked for against
            what the storeroom picked. Server-gated to storeroom/logistics. */}
        <Route
          path="/van-orders"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <VanOrders />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Stock reset — make a physical count the opening balance. Server-gated
            to inventory-ops, because a storeroom reset posts real corrections. */}
        <Route
          path="/stock-reset"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockReset />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Stock levels — a storeroom's ledger balance, or a van's derived
            on-hand. Server-gated to inventory-ops / procurement. */}
        <Route
          path="/stock-levels"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockLevels />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Stock analytics — movement in/out and job-card usage over time.
            Server-gated to inventory-ops / procurement. */}
        {/* Daily Installations — the NEO2 rebuild of the Power BI page. Two
            modes: "Comparable" counts exactly as Power BI does from mirrored
            SharePoint fields so the two can be proved equal; "Full" adds the
            stock and faulty data Power BI has no access to. Operations-gated. */}
        {/* Installations Leaderboard — ranked on the report's own weighted
            score, with the working and the data coverage on show. */}
        <Route
          path="/installations-leaderboard"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Leaderboard />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Target v Performance - banded targets, progress, and the incentive
            payments that follow. Operations-gated like the rest of the module. */}
        {/* Installation Master — rebuilt from the Power BI page of the same name. */}
        <Route
          path="/installation-master"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <InstallationMaster />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Whereabouts Master — rebuilt from the Power BI page of the same name. */}
        <Route
          path="/whereabouts-master"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <WhereaboutsMaster />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Support Requests — rebuilt from the Power BI page of the same name. */}
        <Route
          path="/support-requests"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <SupportRequests />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Internal Remediations — rebuilt from the Power BI page of the same name. */}
        <Route
          path="/internal-remediations"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <InternalRemediations />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Safety & Compliance — rebuilt from the Power BI page of the same name. */}
        <Route
          path="/safety-compliance"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <SafetyCompliance />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Customer Experience — rebuilt from the Power BI page of the same name. */}
        <Route
          path="/customer-experience"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CustomerExperience />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Operations Reporting — the hub for the nine rebuilt pages. */}
        <Route
          path="/ops-reporting"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <OperationsReporting />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Stock Logging KPIs — per engineer and per manager, on completed work. */}
        <Route
          path="/stock-logging-kpis"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockLoggingKpis />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Commercial Surveys: raised and assigned here, answered on FSM. */}
        <Route
          path="/commercial-surveys"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommercialSurveys />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        <Route
          path="/pole-map"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PoleMap />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />

        {/* Pole survey audit log (TKT-00042) — the API limits who can read it. */}
        <Route
          path="/pole-survey/log"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PoleSurveyLog />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />
        {/* Job Imports (29/10/2026, on trial): ODIN's work orders read as a signed-in ODIN user. */}
        <Route
          path="/job-imports"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobImports />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />
        {/* Climb Check: tomorrow's ODIN jobs, climb / hoist / via another pole. */}
        <Route
          path="/climb-check"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ClimbCheck />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />
        {/* Operations: pole survey — Openreach climb status before dispatch. */}
        <Route
          path="/pole-survey"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PoleMap />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />

        {/* Customer Experience: the two CX registers, kept in SharePoint. */}
        {[['ainm', '/cx/ainm'], ['complaint', '/cx/complaints']].map(([reg, path]) => (
          <Route
            key={path}
            path={path}
            element={(
              <>
                <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
                <AuthenticatedTemplate>
                  <AuthenticatedLayout>
                    <CxRegister register={reg} />
                  </AuthenticatedLayout>
                </AuthenticatedTemplate>
              </>
            )}
          />
        ))}


        {/* Fleet registers rebuilt from monday.com (config on the tech server). */}
        <Route
          path="/driver-licences"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <FleetRegister register="licences" />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Fleet: fines and PCNs. */}
        <Route
          path="/fines"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <FleetRegister register="fines" />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Fleet: rental van on/off-hire inspections. */}
        <Route
          path="/rental-inspections"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <FleetRegister register="rentals" />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Fleet: read-only nightly backup of the monday Fleet Master (board 305). */}
        <Route
          path="/fleet-master-backup"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <FleetRegister register="fleetmaster" />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Fleet: weekly walkaround checks against the Fleet Master. */}
        <Route
          path="/walkarounds"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Walkarounds />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Monthly Install Count: completed installs by network, area and week. */}
        <Route
          path="/install-count"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <InstallCount />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        <Route
          path="/target-performance"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <TargetPerformance />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        <Route
          path="/daily-installations"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <DailyInstallations />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        <Route
          path="/stock-analytics"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockAnalytics />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Stock check — the working behind one engineer's van figures, for
            going through with him. Server-gated to storeroom / logistics. */}
        {/* Stock Variances — where a van's count stopped matching its books. */}
        <Route
          path="/stock-variances"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockVariances />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        <Route
          path="/stock-check"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockCheck />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Devices to Collect — Logistics collection queue; server-gated. */}
        <Route
          path="/devices/to-collect"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <DevicesToCollect />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Platform — full-admin only, and deliberately NOT under a department.
            The server enforces that on every request; this route existing just
            means a non-admin sees the screen's own 403 message rather than a
            blank page. */}
        {/* Platform > Page access — per-person page grants (Job Cards first),
            full admins only; the grants live in FSM (routes/pagePermissions.js). */}
        <Route
          path="/platform/page-access"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PageAccess />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/platform/integrations"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Integrations />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/platform/resources"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Resources />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/platform/backups"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Backups />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Comms Dispatch — office/ops send + metrics; the tech API gates it on
            NEO2-Communications-Access / full-admin, so a non-member sees the
            screen's own message rather than a blank page. */}
        {/* Support — live assistance console + AI Assistant Review. Both are
            server-gated on Nano-Support-Access (full admins bypass). */}
        <Route
          path="/support"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <SupportConsole />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/support/assistant"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AssistantReview />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/comms"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommsDispatch />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Message Analytics — list at /comms/analytics; one message's dashboard
            at /comms/analytics/:id (same component reads the param). */}
        <Route
          path="/comms/analytics"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <MessageAnalytics />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/comms/analytics/:id"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <MessageAnalytics />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Recipient Activity — per-user engagement. List = /comms/recipients;
            one person's history = /comms/recipients/:entraId. */}
        <Route
          path="/comms/recipients"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <RecipientActivity />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/comms/recipients/:entraId"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <RecipientActivity />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* My Messages — inbox for any signed-in user. /messages/:id auto-opens a
            message (the email "Open message" button deep-links here). */}
        <Route
          path="/messages"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommsInbox />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/messages/:id"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommsInbox />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* MFA migration call list — IT-group gated server-side (same as the
            MFA progress card). Direct URL handed to whoever is running the drive. */}
        <Route
          path="/mfa"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <MfaCallList />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/isp-stock-counts"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <IspStockCounts />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/stock-disputes"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockDisputes />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/van-transfers"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <VanTransfers />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/job-card-stock"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobCardStock />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/job-card-stock-order"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobCardStockOrder />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/van-stock"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <VanStock />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/job-cards"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobCards />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockProcurement />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/approvals"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ProcurementApprovals />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/helpdesk"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ItSupport />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/back-orders"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <BackOrders />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/receiving"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Receiving />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/inventory"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Inventory />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/platform/products"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Products />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
